import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Search, Eye, EyeOff, Trash2, CheckCircle, XCircle,
  BarChart3, Users, Globe, RefreshCw, ExternalLink, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ScoreBadge({ score }) {
  const color = score >= 75 ? 'bg-green-100 text-green-700' : score >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${color}`}>{score}%</span>;
}

export default function UltraExhibitors() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const limit = 10;

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/ultra/exhibitor-profiles`, {
      params: { page, limit, search: search || undefined },
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      setData(data);
      setItems(data.items || []);
      setTotal(data.total || 0);
    }).catch(() => showToast('error', 'Failed to load exhibitor profiles'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, search]);

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3500); };

  const toggleVisibility = async (item) => {
    try {
      await axios.patch(`${API}/api/ultra/exhibitor-profiles/${item.org_id}/visibility`,
        { is_visible: !item.is_visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('success', `Profile ${!item.is_visible ? 'activated' : 'deactivated'}`);
      load();
    } catch { showToast('error', 'Action failed'); }
  };

  const deleteProfile = async (item) => {
    if (!window.confirm(`Delete "${item.display_name}" profile content? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/ultra/exhibitor-profiles/${item.org_id}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      showToast('success', 'Profile content deleted');
      load();
    } catch { showToast('error', 'Delete failed'); }
  };

  const startEdit = (item) => {
    setEditingId(item.org_id);
    setEditForm({
      tagline: item.profile?.tagline || '',
      about: item.profile?.about || '',
      is_visible: item.is_visible,
    });
  };

  const saveEdit = async (orgId) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/ultra/exhibitor-profiles/${orgId}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      showToast('success', 'Profile updated');
      setEditingId(null);
      load();
    } catch { showToast('error', 'Save failed'); }
    finally { setSaving(false); }
  };

  // Server-side summary stats (accurate across all pages)
  const active = data?.summary?.active ?? items.filter(x => x.is_visible).length;
  const withStands = data?.summary?.with_stands ?? items.filter(x => x.profile?.stands?.length > 0).length;
  const avgScore = items.length ? Math.round(items.reduce((a, x) => a + x.completion_score, 0) / items.length) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exhibitor Profiles</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage and moderate all exhibitor public profiles</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" />Refresh</Button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Building2, label: 'Total Exhibitors', value: total, color: 'bg-blue-50 text-blue-700' },
          { icon: CheckCircle, label: 'Active Profiles', value: active, color: 'bg-green-50 text-green-700' },
          { icon: BarChart3, label: 'Avg. Completion', value: `${avgScore}%`, color: 'bg-purple-50 text-purple-700' },
          { icon: Globe, label: 'With Stand Gallery', value: withStands, color: 'bg-orange-50 text-orange-700' },
        ].map(k => (
          <Card key={k.label} className="border-0 shadow-sm">
            <CardContent className="pt-4 pb-3 flex items-center gap-3">
              <div className={`p-2 rounded-xl ${k.color}`}><k.icon className="h-5 w-5" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{k.value}</p>
                <p className="text-xs text-gray-500">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Search exhibitors…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Building2 className="h-12 w-12 mb-2 opacity-30" />
            <p>No exhibitors found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Exhibitor', 'Location', 'Completion', 'Stands', 'Upcoming', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => (
                  <React.Fragment key={item.org_id}>
                    <tr className={`hover:bg-gray-50 transition-colors ${editingId === item.org_id ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                            {item.display_name?.[0] || 'E'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.display_name}</p>
                            <p className="text-xs text-gray-400">/{item.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {[item.city, item.country].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3"><ScoreBadge score={item.completion_score} /></td>
                      <td className="px-4 py-3 text-gray-600">{item.profile?.stands?.length || 0}</td>
                      <td className="px-4 py-3 text-gray-600">{item.profile?.upcoming_shows?.length || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${item.is_visible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {item.is_visible ? <><CheckCircle className="h-3 w-3" />Active</> : <><XCircle className="h-3 w-3" />Hidden</>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <a href={`/exhibitor/${item.slug}`} target="_blank" rel="noreferrer" title="View public profile">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600"><ExternalLink className="h-3.5 w-3.5" /></Button>
                          </a>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600" onClick={() => editingId === item.org_id ? setEditingId(null) : startEdit(item)} title="Edit inline">
                            <BarChart3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${item.is_visible ? 'text-green-600 hover:text-gray-500' : 'text-gray-400 hover:text-green-600'}`} onClick={() => toggleVisibility(item)} title={item.is_visible ? 'Deactivate' : 'Activate'}>
                            {item.is_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" onClick={() => deleteProfile(item)} title="Delete profile content">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline Edit Row */}
                    {editingId === item.org_id && (
                      <tr className="bg-blue-50/70">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tagline</label>
                              <input className="w-full text-sm px-3 py-2 border border-blue-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.tagline} onChange={e => setEditForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Company tagline…" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Visibility</label>
                              <select className="w-full text-sm px-3 py-2 border border-blue-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.is_visible ? 'true' : 'false'} onChange={e => setEditForm(f => ({ ...f, is_visible: e.target.value === 'true' }))}>
                                <option value="true">Active (visible)</option>
                                <option value="false">Hidden</option>
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">About</label>
                              <textarea className="w-full text-sm px-3 py-2 border border-blue-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 h-20 resize-none" value={editForm.about} onChange={e => setEditForm(f => ({ ...f, about: e.target.value }))} placeholder="Company description…" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => saveEdit(item.org_id)} disabled={saving}>
                              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-gray-500">Showing {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total}</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs px-3 py-1 bg-gray-100 rounded">{page}</span>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
